'use client';

import { useCallback, useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { useGetConversationsQuery } from '@/api/features/conversations';
import { reduxLogger } from '@/core/utils/logger';
import { useTour } from '../hooks/useTour';
import { useCompleteTourWithTracking } from '../hooks/useCompleteTourWithTracking';
import { useTourAutoAdvance } from '../hooks/useTourAutoAdvance';
import { TourHighlight } from '../components/TourHighlight';
import { AssignmentsWelcomeModal } from './AssignmentsWelcomeModal';
import { AssignmentsCompleteModal } from './AssignmentsCompleteModal';
import { ASSIGNMENTS_TOUR_STEPS } from './types';

/** Count of highlight-type steps (for "Step X of Y" display) */
const HIGHLIGHT_STEPS = ASSIGNMENTS_TOUR_STEPS.filter((s) => s.type === 'highlight');

/**
 * AssignmentsTourOverlay - Renders the Assignments tour step UI.
 *
 * Only renders when the active tour is 'assignments'. Handles dynamic
 * navigation to the Voley Support conversation for assignment demos.
 */
export function AssignmentsTourOverlay() {
  const router = useRouter();
  const { isActive, activeTourId, currentStep, nextStep } = useTour();
  const { completeTourWithTracking } = useCompleteTourWithTracking();

  const isAssignmentsTour = activeTourId === 'assignments';

  // Auto-advance polling
  useTourAutoAdvance(isAssignmentsTour, currentStep, nextStep);

  // Find the Voley Support conversation for navigation
  const { data: searchResults } = useGetConversationsQuery(
    { search: 'Voley Support', limit: 1 },
    { skip: !isAssignmentsTour, refetchOnMountOrArgChange: 5 * 60 },
  );
  const voleySupportConversation = searchResults?.items?.[0];
  const voleySupportId = voleySupportConversation?.id;

  // Track which steps we've already navigated for (to avoid re-navigating)
  const navigatedForStepRef = useRef<string | null>(null);

  // Handle all step-specific navigation explicitly in the overlay
  useEffect(() => {
    if (!isAssignmentsTour || !currentStep) return;
    if (navigatedForStepRef.current === currentStep.id) return;

    // Steps that need Voley Support conversation open
    const stepsNeedingConversation = ['highlight_assign_btn'];
    if (stepsNeedingConversation.includes(currentStep.id) && voleySupportId) {
      navigatedForStepRef.current = currentStep.id;
      router.push(`/dashboard?view=all&conversation=${voleySupportId}`);
      reduxLogger.info(`[AssignmentsTour] Navigating to Voley Support for step: ${currentStep.id}`);
      return;
    }

    // Steps that need view-level navigation (no conversation)
    const viewNavigationMap: Record<string, string> = {
      assigned_view: '/dashboard?view=assigned',
      highlight_filter_btn: '/dashboard?view=all',
    };
    const targetUrl = viewNavigationMap[currentStep.id];
    if (targetUrl) {
      navigatedForStepRef.current = currentStep.id;
      router.push(targetUrl);
      reduxLogger.info(`[AssignmentsTour] Navigating to ${targetUrl} for step: ${currentStep.id}`);
    }
  }, [isAssignmentsTour, currentStep?.id, currentStep, voleySupportId, router]);

  const handleWelcomeComplete = useCallback(() => {
    if (voleySupportId) {
      router.push(`/dashboard?view=all&conversation=${voleySupportId}`);
      reduxLogger.info('[AssignmentsTour] Navigating to Voley Support conversation');
    } else {
      router.push('/dashboard?view=all');
      reduxLogger.warn('[AssignmentsTour] Voley Support conversation not found, navigating to dashboard');
    }
    nextStep();
  }, [voleySupportId, router, nextStep]);

  const handleFinish = useCallback(() => {
    completeTourWithTracking('assignments');
    router.push('/dashboard?view=all');
  }, [completeTourWithTracking, router]);

  const handleHighlightNext = useCallback(() => {
    nextStep();
  }, [nextStep]);

  if (!isAssignmentsTour || !isActive || !currentStep) return null;

  // Backdrop visibility — block clicks only for non-interactive, non-auto-advancing highlight steps
  const isHighlightStep = currentStep.type === 'highlight';
  const isClickThrough = !!(
    currentStep.advanceOnNavigate ||
    currentStep.advanceOnElement ||
    currentStep.advanceOnElementRemoved
  );
  const isInteractive = !!currentStep.allowInteraction;
  const showBackdrop = isHighlightStep && !isClickThrough && !isInteractive;

  return (
    <>
      {/* Persistent backdrop */}
      <div
        className="fixed inset-0 transition-opacity duration-200"
        style={{
          zIndex: 199,
          pointerEvents: showBackdrop ? 'auto' : 'none',
          opacity: showBackdrop ? 1 : 0,
        }}
      />

      {/* Step content */}
      {currentStep.type === 'modal' && (
        <>
          {currentStep.id === 'assignments_welcome' && <AssignmentsWelcomeModal onComplete={handleWelcomeComplete} />}
          {currentStep.id === 'assignments_complete' && <AssignmentsCompleteModal onFinish={handleFinish} />}
        </>
      )}

      {currentStep.type === 'highlight' &&
        (() => {
          const highlightIndex = HIGHLIGHT_STEPS.findIndex((s) => s.id === currentStep.id);
          return (
            <TourHighlight
              step={currentStep}
              stepNumber={highlightIndex + 1}
              totalSteps={HIGHLIGHT_STEPS.length}
              onNext={handleHighlightNext}
              isLastStep={highlightIndex === HIGHLIGHT_STEPS.length - 1}
            />
          );
        })()}
    </>
  );
}
